import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { Search, X, MessageCircleWarning } from "lucide-react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const WakeWordDialog = ({ open, onClose, onSelect, wakeWords = [] }) => {
  const [filteredWakeWords, setFilteredWakeWords] = useState([]);
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const extractWakeWords = (data) => {
    if (!Array.isArray(data)) return [];

    return data
      .map((item) => {
        if (item && typeof item === "object" && "wakeWord" in item) {
          return item.wakeWord ? String(item.wakeWord) : "";
        }
        return item != null ? String(item) : "";
      })
      .filter(Boolean);
  };

  useEffect(() => {
    const wordList = extractWakeWords(wakeWords);
    setFilteredWakeWords(wordList);
  }, [open, wakeWords]);

  useEffect(() => {
    if (open) {
      setSearchText("");
      const wordList = extractWakeWords(wakeWords);
      setFilteredWakeWords(wordList);
    }
  }, [open, wakeWords]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (value) {
      const wordList = extractWakeWords(wakeWords);
      const filtered = wordList.filter((word) =>
        word.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredWakeWords(filtered);
    } else {
      const wordList = extractWakeWords(wakeWords);
      setFilteredWakeWords(wordList);
    }
  };

  const handleWakeWord = (word) => {
    if (onSelect) {
      onSelect(word);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          height: fullScreen ? "100%" : "80vh",
          maxHeight: "none",
          bgcolor: theme.palette.background.default,
          "& .MuiDialogContent-root": {
            bgcolor: theme.palette.background.default,
          },
          "& .MuiDialogTitle-root": {
            bgcolor: theme.palette.background.default,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 48,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          size="small"
        >
          <X size={18} />
        </IconButton>
        <Typography variant="subtitle1" component="div" sx={{ flex: 1 }}>
          Quick Words
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          p: "16px !important",
          gap: 2,
          overflow: "hidden",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 2,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search quick words..."
            value={searchText}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} style={{ color: "#666666" }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: "background.input",
                borderRadius: 2,
              },
            }}
            size="medium"
            sx={{
              height: "100%",
              "& .MuiOutlinedInput-root": {
                height: "100%",
              },
            }}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            borderRadius: 2,
            gap: 1,
            p: 2,
          }}
        >
          {filteredWakeWords.length > 0 ? (
            <List
              sx={{
                width: "100%",
                bgcolor: "background.paper",
                overflow: "auto",
                flex: 1,
                "& .MuiListItem-root": {
                  py: 1,
                  px: 1,
                },
              }}
            >
              {filteredWakeWords.map((word, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleWakeWord(word)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <MessageCircleWarning
                    size={16}
                    style={{ marginRight: 12, opacity: 0.8, color: "#666666" }}
                  />
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          fontSize: "1.25rem",
                          lineHeight: 1.5,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {word}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                color: "text.secondary",
                backgroundColor: "background.paper",
                borderRadius: 1,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {searchText
                  ? "No matching quick words found"
                  : "No quick words available"}
              </Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default WakeWordDialog;
